'use client';

import { Card } from '@/components/Card';
import { useCurrency } from '@/contexts/currencyContext';
import { useLocale } from '@/hooks/useLocale';
import { Experience, ExperienceTranslation } from '@/services/experienceTypes';
import { ShortListMedia } from '@/types/Experience';
import { toShortListMedia } from '@/utils/shortlistUtils';
import parser from 'html-react-parser';
import { usePathname } from 'next/navigation';
import React, { HTMLAttributes } from 'react';
import styles from './styles.module.css';

export type ShortList = {
  id: number;
  title: string;
  titleSlugs: {
    [key: string]: string;
  }[];
  categoryId: number;
  media: ShortListMedia | string;
  zone: {
    code: string;
  };
  price: number;
  currency: string;
  city: string;
  duration: string;
  languagens: {
    id: number;
    code: string;
    description: string;
  }[];
  minGuests: number;
  maxGuests: number;
};

interface IExperienceTypeProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  description: string;
  experiences: Experience[];
  favourites?: ShortList[];
  setFavourites?: (value: ShortList[]) => void;
}

export const ExperienceType: React.FC<IExperienceTypeProps> = ({
  title,
  description,
  experiences,
  favourites = [],
  setFavourites,
}) => {
  return (
    <div id="type" className="desktop:ml-24">
      <h2 className="mb-8 text-4xl font-noe">{title}</h2>
      <div className="mb-12">
        <div>
          <div className={styles.description}>
            <div className="mb-4">{parser(description)}</div>
          </div>
        </div>
        <div className="grid grid-cols-1 tablet:grid-cols-2 desktop:grid-cols-3 xl:grid-cols-4 gap-8 mt-16 mb-8 h-auto">
          <FavouriteCard
            experiences={experiences}
            favourites={favourites}
            setFavourites={setFavourites}
          />
        </div>
      </div>
    </div>
  );
};

function FavouriteCard({
  experiences,
  favourites,
  setFavourites,
}: {
  experiences: Experience[];
  favourites: ShortList[];
  setFavourites?: (value: ShortList[]) => void;
}) {
  const lang = useLocale();
  const { currency } = useCurrency();
  const path = usePathname();
  const adjustedPath = lang !== 'en' ? path.slice(3) : path;

  const toggleFavorite = (exp: Experience) => {
    const translation = exp.experienceTranslation.find((item) => {
      if (item.locale === lang) {
        return item;
      }
    }) as ExperienceTranslation;

    const titleSlugs = exp.experienceTranslation.map((item) => ({
      [item.locale]: item.slug,
      title: item.title,
    }));

    const isFavourite = favourites.some((item) => item.id === exp.id);

    const coverMedia =
      exp.media.find((m) => m.type === 'cover') ?? exp.media[0];

    let firstPrice = null;

    exp.optionMaps.some((option) => {
      const price = option.prices.find((price) => price.price);
      if (price) {
        firstPrice = price.price;
        return true;
      }
    });

    const shortListInfos: ShortList = {
      id: exp.id,
      title: translation.title,
      titleSlugs: titleSlugs,
      categoryId: exp.macroCategory.id,
      media: toShortListMedia(coverMedia),
      zone: {
        code: exp.zone ? exp.zone.code : '',
      },
      price: Number(firstPrice),
      currency: currency.value,
      city: exp.city,
      duration: exp.duration,
      languagens: exp.languages,
      minGuests: exp.minParticipants,
      maxGuests: exp.maxParticipants,
    };

    if (isFavourite) {
      const updatedFavourites = favourites.filter((item) => item.id !== exp.id);
      setFavourites?.(updatedFavourites);
      localStorage.setItem('experience', JSON.stringify(updatedFavourites));
    } else {
      const updatedFavourites = [...favourites, shortListInfos];
      setFavourites?.(updatedFavourites);
      localStorage.setItem('experience', JSON.stringify(updatedFavourites));
    }
  };

  return experiences.map((experience, index) => {
    if (experience.id === 11) {
      return;
    }

    const translation = experience.experienceTranslation.find((item) => {
      if (item.locale === lang) {
        return item;
      }
    }) as ExperienceTranslation;

    const coverMedia = experience.media.find((m) => m.type === 'cover');

    const isFavourite = favourites.some(
      (item: { id: number }) => item.id === experience.id,
    );

    return (
      <Card
        key={index}
        cardMedia={{
          media: coverMedia,
          filterName: ['large_3-2', '1920w', '1920w_webp'],
          createFilterName: 'large_3-2',
        }}
        hasFavouriteButton={true}
        onClickFavourite={() => toggleFavorite(experience)}
        isFavourite={isFavourite}
        href={`${adjustedPath}/${translation.slug}`}
        hasSubtitle
        customStyle="max-h-[420px] min-w-[270px] w-full before:h-[80%] before:bg-header-gradient hover:mb-3 hover:-mt-3"
        sizes="(min-width: 640px) 50vw, (min-width: 800px) 33vw, (min-width: 1200px) 25vw, 100vw"
        hoverContent={
          <div className="w-full whitespace-normal text-center text-sm max-h-0 pb-4">
            {parser(translation.description)}
          </div>
        }>
        <span className="text-subtitle w-full box-border whitespace-normal font-averta-semibold">
          {translation.title}
        </span>
      </Card>
    );
  });
}
